import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    ButtonGroup,
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerOverlay,
    Flex,
    Grid,
    GridItem,
    HStack,
    Heading,
    Icon,
    IconButton,
    Spinner,
    Text,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import {
    FiChevronLeft,
    FiChevronRight,
    FiFilter,
    FiPackage,
    FiShoppingBag,
    FiSliders,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { Product } from "../../types/product";
import { useGetProductsQuery } from "../../productApi/productAPI";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import {
    addToCart,
    increaseQuantity,
} from "../../store/cartSlice/cartSlice";
import { fetchAllTrips } from "../../store/travelSlice/hikeTripsSlice";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useCombinedStore } from "../../store/hooks/useCombinedStore";
import BusinessListingShowcaseCard from "../Businesses/BusinessListingShowcaseCard";
import ListingFilterSidebar, {
    BusinessListingFilterOption,
    BusinessListingFilters,
} from "../Businesses/ListingFilterSidebar";
import ReusableStyledTabs, {
    ReusableStyledTabItem,
} from "../Shared/ReusableStyledTabs";

const ChevronLeftIcon = FiChevronLeft as unknown as React.ElementType;
const ChevronRightIcon = FiChevronRight as unknown as React.ElementType;
const FilterIcon = FiFilter as unknown as React.ElementType;
const PackageIcon = FiPackage as unknown as React.ElementType;
const ShoppingBagIcon = FiShoppingBag as unknown as React.ElementType;
const SlidersIcon = FiSliders as unknown as React.ElementType;
const cardsBorderRadii = "8px";

type MarketplaceListingType = "products" | "accommodations" | "carHire" | "trips";

type MarketplaceTab = {
    key: MarketplaceListingType;
    label: string;
    type: MarketplaceListingType;
    items: any[];
};

type FiltersByTab = Record<MarketplaceListingType, BusinessListingFilters>;

const ITEMS_PER_PAGE = 30;

const DEFAULT_FILTERS: BusinessListingFilters = {
    search: "",
    categories: [],
    priceRange: [0, 0],
    minDiscount: 0,
    deliveryEligibleOnly: false,
    availableOnly: false,
    location: "",
    sortBy: "recommended",
};

const createDefaultFiltersByTab = (): FiltersByTab => ({
    products: { ...DEFAULT_FILTERS },
    accommodations: { ...DEFAULT_FILTERS },
    carHire: { ...DEFAULT_FILTERS },
    trips: { ...DEFAULT_FILTERS },
});

const TRIP_PLACEHOLDER_IMAGE = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg width="800" height="520" viewBox="0 0 800 520" fill="none" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="800" y2="520" gradientUnits="userSpaceOnUse">
      <stop stop-color="#0F766E"/>
      <stop offset="1" stop-color="#2563EB"/>
    </linearGradient>
    <linearGradient id="card" x1="180" y1="130" x2="620" y2="390" gradientUnits="userSpaceOnUse">
      <stop stop-color="white" stop-opacity="0.24"/>
      <stop offset="1" stop-color="white" stop-opacity="0.08"/>
    </linearGradient>
  </defs>
  <rect width="800" height="520" rx="34" fill="url(#bg)"/>
  <circle cx="122" cy="110" r="70" fill="white" fill-opacity="0.08"/>
  <circle cx="706" cy="420" r="95" fill="white" fill-opacity="0.08"/>
  <rect x="160" y="122" width="480" height="276" rx="34" fill="url(#card)" stroke="white" stroke-opacity="0.22" stroke-width="2"/>
  <path d="M248 294C295 240 348 240 400 294C452 348 505 348 552 294" stroke="white" stroke-width="18" stroke-linecap="round"/>
  <circle cx="248" cy="294" r="24" fill="white"/>
  <circle cx="552" cy="294" r="24" fill="white"/>
  <path d="M333 206H467C492 206 513 227 513 252V294H287V252C287 227 308 206 333 206Z" fill="white" fill-opacity="0.92"/>
  <path d="M320 294H480" stroke="#0F766E" stroke-width="18" stroke-linecap="round"/>
  <circle cx="338" cy="324" r="22" fill="#0F172A"/>
  <circle cx="462" cy="324" r="22" fill="#0F172A"/>
  <text x="400" y="452" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="34" font-weight="700">Upcoming Trip</text>
</svg>
`)}`;

const getTodayStartTime = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return today.getTime();
};

const valueToDisplayText = (value: any): string => {
    if (!value) return "";

    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);

    if (typeof value === "object") {
        return (
            value.address ||
            value.name ||
            value.label ||
            value.formattedAddress ||
            value.description ||
            ""
        );
    }

    return String(value);
};

const parseDateTime = (rawDate: any, rawTime?: any) => {
    if (!rawDate) return 0;

    const dateText = String(rawDate);

    if (/^\d{4}-\d{2}-\d{2}/.test(dateText)) {
        const [year, month, day] = dateText.slice(0, 10).split("-").map(Number);
        const parsedDate = new Date(year, month - 1, day);

        if (rawTime && typeof rawTime === "string" && rawTime.includes(":")) {
            const [hours, minutes] = rawTime.split(":").map(Number);
            parsedDate.setHours(hours || 0, minutes || 0, 0, 0);
        } else {
            parsedDate.setHours(0, 0, 0, 0);
        }

        return parsedDate.getTime();
    }

    const time = new Date(dateText).getTime();

    return Number.isFinite(time) ? time : 0;
};

const formatDateLabel = (rawDate: any) => {
    const timestamp = parseDateTime(rawDate);

    if (!timestamp) return "";

    return new Date(timestamp).toLocaleDateString(undefined, {
        weekday: "short",
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const formatTimeLabel = (rawTime?: string) => {
    if (!rawTime) return "";

    const [hours, minutes] = rawTime.split(":").map(Number);

    if (!Number.isFinite(hours)) return "";

    const date = new Date();
    date.setHours(hours || 0, minutes || 0, 0, 0);

    return date.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatTripDateTimeLabel = (item: any) => {
    const dateLabel = formatDateLabel(
        item?.departDate ||
        item?.date ||
        item?.tripDate ||
        item?.departureDate ||
        item?.startDate ||
        item?.travelDate ||
        item?.scheduledDate
    );

    const departTime = formatTimeLabel(item?.departTime);
    const arriveTime = formatTimeLabel(item?.arriveTime);

    if (dateLabel && departTime && arriveTime) {
        return `${dateLabel} • ${departTime} - ${arriveTime}`;
    }

    if (dateLabel && departTime) {
        return `${dateLabel} • ${departTime}`;
    }

    return dateLabel;
};

const resolveId = (item: any) => item?._id || item?.id;

const resolveTitle = (item: any) => {
    const fromText = valueToDisplayText(item?.from);
    const toText = valueToDisplayText(item?.to);

    if (fromText && toText) {
        return `${fromText} to ${toText}`;
    }

    return (
        valueToDisplayText(item?.title) ||
        valueToDisplayText(item?.name) ||
        valueToDisplayText(item?.productName) ||
        valueToDisplayText(item?.accommodationName) ||
        valueToDisplayText(item?.hotelName) ||
        valueToDisplayText(item?.roomName) ||
        valueToDisplayText(item?.tripName) ||
        valueToDisplayText(item?.carName) ||
        valueToDisplayText(item?.vehicleName) ||
        valueToDisplayText(item?.model) ||
        "Untitled"
    );
};

const resolveDescription = (item: any, type?: MarketplaceListingType) => {
    if (type === "trips") {
        const tripDate = formatTripDateTimeLabel(item);
        const carType = valueToDisplayText(item?.carType);
        const seatsNumber = Number(
            item?.seatsAvailable ?? item?.availableSeats ?? item?.seats ?? 0
        );
        const seats =
            seatsNumber > 0
                ? `${seatsNumber} seat${seatsNumber === 1 ? "" : "s"} left`
                : "";

        return [tripDate, carType, seats].filter(Boolean).join(" • ");
    }

    return (
        valueToDisplayText(item?.description) ||
        valueToDisplayText(item?.summary) ||
        valueToDisplayText(item?.details) ||
        valueToDisplayText(item?.about) ||
        valueToDisplayText(item?.carType) ||
        ""
    );
};

const resolveImage = (item: any, type?: MarketplaceListingType) => {
    const image =
        valueToDisplayText(item?.selectedImage) ||
        valueToDisplayText(item?.imageUrl) ||
        valueToDisplayText(item?.image) ||
        valueToDisplayText(item?.mainImage) ||
        valueToDisplayText(item?.coverImage) ||
        valueToDisplayText(item?.images?.[0]) ||
        valueToDisplayText(item?.photos?.[0]) ||
        valueToDisplayText(item?.gallery?.[0]) ||
        "";

    if (!image && type === "trips") {
        return TRIP_PLACEHOLDER_IMAGE;
    }

    return image;
};

const resolveLocation = (item: any) => {
    const fromText = valueToDisplayText(item?.from);
    const toText = valueToDisplayText(item?.to);

    if (fromText && toText) {
        return `${fromText} → ${toText}`;
    }

    return (
        valueToDisplayText(item?.location) ||
        valueToDisplayText(item?.address) ||
        valueToDisplayText(item?.city) ||
        valueToDisplayText(item?.area) ||
        fromText ||
        toText ||
        valueToDisplayText(item?.pickupLocation) ||
        valueToDisplayText(item?.sellerLocation) ||
        ""
    );
};

const resolvePrice = (item: any) => {
    const value =
        item?.selectedPrice ??
        item?.price ??
        item?.priceMk ??
        item?.priceMWK ??
        item?.amount ??
        item?.cost ??
        item?.rate ??
        item?.pricePerNight ??
        item?.nightlyRate ??
        item?.dailyRate ??
        item?.hireRate ??
        item?.seatPrice ??
        item?.fare ??
        "";

    return value;
};

const resolveNumericPrice = (item: any) => {
    const rawPrice = resolvePrice(item);

    if (typeof rawPrice === "number") return rawPrice;

    const parsedPrice = Number(String(rawPrice).replace(/[^\d.]/g, ""));

    return Number.isFinite(parsedPrice) ? parsedPrice : 0;
};

const resolveCategory = (item: any, type?: MarketplaceListingType) => {
    if (type === "trips") {
        return valueToDisplayText(item?.carType) || "Trip";
    }

    return (
        valueToDisplayText(item?.category) ||
        valueToDisplayText(item?.type) ||
        valueToDisplayText(item?.carType) ||
        valueToDisplayText(item?.vehicleType) ||
        valueToDisplayText(item?.roomType) ||
        valueToDisplayText(item?.accommodationType) ||
        valueToDisplayText(item?.tripType) ||
        "Other"
    );
};

const resolveDiscount = (item: any) => {
    const rawDiscount =
        item?.discountPercentage ??
        item?.discountPercent ??
        item?.discount ??
        item?.percentageOff ??
        0;

    const discount = Number(rawDiscount);

    return Number.isFinite(discount) ? discount : 0;
};

const resolveCreatedAt = (item: any) => {
    const rawDate =
        item?.createdAt ||
        item?.updatedAt ||
        item?.dateCreated ||
        item?.created_at ||
        "";

    const time = new Date(rawDate).getTime();

    return Number.isFinite(time) ? time : 0;
};

const resolveTripDateTime = (item: any) => {
    const rawDate =
        item?.departDate ||
        item?.date ||
        item?.tripDate ||
        item?.departureDate ||
        item?.startDate ||
        item?.travelDate ||
        item?.scheduledDate ||
        "";

    return parseDateTime(rawDate, item?.departTime);
};

const isFutureTrip = (item: any) => {
    const tripTime = resolveTripDateTime(item);

    if (!tripTime) return false;

    return tripTime >= getTodayStartTime();
};

const isDeliveryEligible = (item: any, type?: MarketplaceListingType) => {
    if (type !== "products") return true;

    return Boolean(
        item?.eligibleForDelivery ||
        item?.deliveryEligible ||
        item?.canDeliver ||
        item?.supportsDelivery ||
        item?.deliveryAvailable ||
        item?.delivery?.eligible ||
        item?.delivery?.available
    );
};

const isAvailable = (item: any, type?: MarketplaceListingType) => {
    if (type === "trips") {
        return (
            Number(item?.seatsAvailable ?? item?.availableSeats ?? item?.seats ?? 0) > 0
        );
    }

    if (type === "carHire") {
        if (typeof item?.available === "boolean") return item.available;
        if (typeof item?.isAvailable === "boolean") return item.isAvailable;

        return true;
    }

    if (type === "accommodations") {
        if (typeof item?.available === "boolean") return item.available;
        if (typeof item?.isAvailable === "boolean") return item.isAvailable;
        if (typeof item?.roomsAvailable === "number") return item.roomsAvailable > 0;

        return true;
    }

    if (typeof item?.available === "boolean") return item.available;
    if (typeof item?.isAvailable === "boolean") return item.isAvailable;
    if (typeof item?.inStock === "boolean") return item.inStock;

    if (typeof item?.stock === "number") return item.stock > 0;
    if (typeof item?.quantity === "number") return item.quantity > 0;

    return true;
};

const normaliseText = (value: unknown) =>
    valueToDisplayText(value).toLowerCase().trim();

const itemMatchesSearch = (
    item: any,
    search: string,
    type: MarketplaceListingType
) => {
    const query = normaliseText(search);

    if (!query) return true;

    const searchableText = [
        resolveTitle(item),
        resolveDescription(item, type),
        resolveCategory(item, type),
        resolveLocation(item),
        item?.brand,
        item?.model,
        item?.businessName,
        item?.sellerName,
        item?.from,
        item?.to,
        item?.departDate,
        item?.carType,
    ]
        .map(normaliseText)
        .join(" ");

    return searchableText.includes(query);
};

const itemMatchesLocation = (item: any, location: string) => {
    const query = normaliseText(location);

    if (!query) return true;

    const locationText = [
        resolveLocation(item),
        item?.address,
        item?.city,
        item?.area,
        item?.from,
        item?.to,
        item?.pickupLocation,
        item?.dropoffLocation,
        item?.sellerLocation,
    ]
        .map(normaliseText)
        .join(" ");

    return locationText.includes(query);
};

const getPriceBounds = (items: any[]): [number, number] => {
    const prices = items
        .map(resolveNumericPrice)
        .filter((price) => Number.isFinite(price) && price > 0);

    if (!prices.length) return [0, 0];

    return [Math.min(...prices), Math.max(...prices)];
};

const getDefaultFiltersForItems = (items: any[]): BusinessListingFilters => {
    const [minPrice, maxPrice] = getPriceBounds(items);

    return {
        ...DEFAULT_FILTERS,
        priceRange: [minPrice, maxPrice],
    };
};

const getCategoryOptions = (
    items: any[],
    type: MarketplaceListingType
): BusinessListingFilterOption[] => {
    const categoryCounts = items.reduce<Record<string, number>>((acc, item) => {
        const category = resolveCategory(item, type);

        acc[category] = (acc[category] || 0) + 1;

        return acc;
    }, {});

    return Object.entries(categoryCounts)
        .map(([category, count]) => ({
            value: category,
            label: category,
            count,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
};

const applyListingFilters = (
    items: any[],
    filters: BusinessListingFilters,
    type: MarketplaceListingType
): any[] => {
    const filteredItems = items.filter((item) => {
        const price = resolveNumericPrice(item);
        const category = resolveCategory(item, type);
        const discount = resolveDiscount(item);

        const matchesSearch = itemMatchesSearch(item, filters.search, type);
        const matchesLocation = itemMatchesLocation(item, filters.location);
        const matchesCategory =
            filters.categories.length === 0 || filters.categories.includes(category);
        const matchesPrice =
            price >= filters.priceRange[0] && price <= filters.priceRange[1];
        const matchesDiscount =
            type === "trips" || type === "carHire"
                ? true
                : discount >= filters.minDiscount;
        const matchesDelivery =
            type === "products"
                ? !filters.deliveryEligibleOnly || isDeliveryEligible(item, type)
                : true;
        const matchesAvailability =
            !filters.availableOnly || isAvailable(item, type);

        return (
            matchesSearch &&
            matchesLocation &&
            matchesCategory &&
            matchesPrice &&
            matchesDiscount &&
            matchesDelivery &&
            matchesAvailability
        );
    });

    return [...filteredItems].sort((firstItem, secondItem) => {
        if (filters.sortBy === "priceLowHigh") {
            return resolveNumericPrice(firstItem) - resolveNumericPrice(secondItem);
        }

        if (filters.sortBy === "priceHighLow") {
            return resolveNumericPrice(secondItem) - resolveNumericPrice(firstItem);
        }

        if (filters.sortBy === "discountHighLow") {
            return resolveDiscount(secondItem) - resolveDiscount(firstItem);
        }

        if (filters.sortBy === "newest") {
            if (type === "trips") {
                return resolveTripDateTime(firstItem) - resolveTripDateTime(secondItem);
            }

            return resolveCreatedAt(secondItem) - resolveCreatedAt(firstItem);
        }

        if (type === "trips") {
            return resolveTripDateTime(firstItem) - resolveTripDateTime(secondItem);
        }

        return 0;
    });
};

const getPageNumbers = (currentPage: number, totalPages: number) => {
    if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));

    return Array.from({ length: 5 }, (_, index) => start + index);
};

const getTabActionLabel = (type: MarketplaceListingType) => {
    if (type === "products") return "Add";
    if (type === "accommodations") return "Book";
    if (type === "carHire") return "Hire";
    return "Book";
};

const getTabBadgeLabel = (type: MarketplaceListingType) => {
    if (type === "products") return "Product";
    if (type === "accommodations") return "Stay";
    if (type === "carHire") return "Car Hire";
    return "Trip";
};

const getItemRoute = (type: MarketplaceListingType, id: string) => {
    if (type === "products") return `/product/${id}`;
    if (type === "accommodations") return `/search-details/accommodations/${id}`;
    if (type === "carHire") return `/search-details/cars/${id}`;
    return `/hike/checkout/${id}`;
};

const AllProductsPageHeader = ({
    totalItems,
    filteredItems,
}: {
    totalItems: number;
    filteredItems: number;
}) => {
    const { theme } = useCustomTheme();

    return (
        <Flex
            align={{ base: "flex-start", md: "center" }}
            justify="space-between"
            direction={{ base: "column", md: "row" }}
            gap={4}
            mb={6}
            bg={theme.surface}
            border={`1px solid ${theme.border}`}
            borderRadius={cardsBorderRadii}
            px={{ base: 4, md: 5 }}
            py={{ base: 4, md: 5 }}
            boxShadow={theme.shadowSm}
        >
            <Box maxW={{ base: "100%", md: "58%" }}>
                <Heading
                    size={{ base: "md", md: "lg" }}
                    color={theme.text}
                    lineHeight="1.15"
                    mb={2}
                >
                    Marketplace
                </Heading>

                <Text
                    color={theme.textMuted}
                    fontSize={{ base: "sm", md: "md" }}
                    lineHeight="1.6"
                >
                    Browse products, accommodation, car hire, and upcoming trips available
                    in the application. Use filters, sorting, categories, and pagination
                    to find what you need faster.
                </Text>
            </Box>

            <Flex
                align="center"
                gap={3}
                bg={theme.background}
                border={`1px solid ${theme.border}`}
                borderRadius={cardsBorderRadii}
                px={{ base: 3, md: 4 }}
                py={3}
                w={{ base: "100%", md: "auto" }}
                minW={{ base: "100%", md: "300px" }}
                maxW={{ base: "100%", md: "400px" }}
            >
                <Flex
                    boxSize={{ base: "46px", md: "52px" }}
                    borderRadius={cardsBorderRadii}
                    align="center"
                    justify="center"
                    bg={theme.primarySoft}
                    color={theme.primary}
                    border={`1px solid ${theme.border}`}
                    flexShrink={0}
                >
                    <Icon as={ShoppingBagIcon} boxSize={5} />
                </Flex>

                <Box minW={0}>
                    <Text
                        color={theme.text}
                        fontWeight="900"
                        fontSize={{ base: "sm", md: "md" }}
                        noOfLines={1}
                    >
                        All marketplace listings
                    </Text>

                    <Text
                        color={theme.textMuted}
                        fontSize="xs"
                        fontWeight="700"
                        noOfLines={1}
                    >
                        {filteredItems} result
                        {filteredItems === 1 ? "" : "s"} from {totalItems} listing
                        {totalItems === 1 ? "" : "s"}
                    </Text>

                    <Text
                        color={theme.textSubtle}
                        textTransform="capitalize"
                        fontWeight="800"
                        fontSize="xs"
                        noOfLines={1}
                    >
                        Marketplace
                    </Text>
                </Box>
            </Flex>
        </Flex>
    );
};

const ListingGrid = ({
    items,
    type,
    onAddProductToCart,
}: {
    items: any[];
    type: MarketplaceListingType;
    onAddProductToCart: (item: Product) => void;
}) => {
    const navigate = useNavigate();

    if (!items.length) {
        return null;
    }

    return (
        <Grid
            templateColumns={{
                base: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
                xl: "repeat(4, 1fr)",
            }}
            gap={{ base: 4, md: 5 }}
        >
            {items.map((item) => {
                const id = resolveId(item);
                const route = getItemRoute(type, id);

                return (
                    <GridItem key={id}>
                        <BusinessListingShowcaseCard
                            title={resolveTitle(item)}
                            subtitle={
                                type === "trips"
                                    ? formatTripDateTimeLabel(item)
                                    : resolveCategory(item, type)
                            }
                            description={resolveDescription(item, type)}
                            image={resolveImage(item, type)}
                            price={resolvePrice(item)}
                            location={resolveLocation(item)}
                            category={resolveCategory(item, type)}
                            badgeLabel={getTabBadgeLabel(type)}
                            actionLabel={getTabActionLabel(type)}
                            onCardClick={() => navigate(route)}
                            onActionClick={() => {
                                if (type === "products") {
                                    onAddProductToCart(item as Product);
                                    return;
                                }

                                navigate(route);
                            }}
                        />
                    </GridItem>
                );
            })}
        </Grid>
    );
};

const EmptyFilteredState = ({
    onResetFilters,
    activeType,
}: {
    onResetFilters: () => void;
    activeType: MarketplaceListingType;
}) => {
    const { theme } = useCustomTheme();

    const title =
        activeType === "products"
            ? "No products match these filters"
            : activeType === "accommodations"
                ? "No accommodations match these filters"
                : activeType === "carHire"
                    ? "No car hire listings match these filters"
                    : "No future trips match these filters";

    return (
        <Box
            bg={theme.surface}
            border={`1px solid ${theme.border}`}
            borderRadius={cardsBorderRadii}
            p={{ base: 5, md: 8 }}
            textAlign="center"
        >
            <Flex
                boxSize="52px"
                mx="auto"
                mb={4}
                borderRadius={cardsBorderRadii}
                align="center"
                justify="center"
                bg={theme.primarySoft}
                color={theme.primary}
            >
                <Icon as={PackageIcon} boxSize={5} />
            </Flex>

            <Heading size="sm" color={theme.text} mb={2}>
                {title}
            </Heading>

            <Text color={theme.textMuted} mb={5}>
                Try changing the price range, category, availability, location, or search
                term.
            </Text>

            <Button
                onClick={onResetFilters}
                borderRadius="5px"
                color={theme.textOnPrimary}
                bgImage={theme.gradients.primaryButton}
                _hover={{
                    bgImage: theme.gradients.primaryButtonHover,
                }}
            >
                Reset filters
            </Button>
        </Box>
    );
};

const ListingsPagination = ({
    currentPage,
    totalPages,
    visibleCount,
    totalCount,
    canViewMore,
    onPageChange,
    onViewMore,
}: {
    currentPage: number;
    totalPages: number;
    visibleCount: number;
    totalCount: number;
    canViewMore: boolean;
    onPageChange: (page: number) => void;
    onViewMore: () => void;
}) => {
    const { theme } = useCustomTheme();

    if (totalCount <= ITEMS_PER_PAGE) {
        return null;
    }

    const pageNumbers = getPageNumbers(currentPage, totalPages);

    return (
        <Flex
            mt={6}
            gap={4}
            align={{ base: "stretch", md: "center" }}
            justify="space-between"
            direction={{ base: "column", lg: "row" }}
            bg={theme.surface}
            border={`1px solid ${theme.border}`}
            borderRadius={cardsBorderRadii}
            p={{ base: 4, md: 5 }}
        >
            <Box>
                <Text color={theme.text} fontWeight="900" fontSize="sm">
                    Showing {visibleCount} of {totalCount}
                </Text>

                <Text color={theme.textMuted} fontSize="xs" fontWeight="700">
                    Page {currentPage} of {totalPages}. Showing {ITEMS_PER_PAGE} items per
                    page.
                </Text>
            </Box>

            <Flex
                gap={3}
                align={{ base: "stretch", sm: "center" }}
                justify={{ base: "stretch", lg: "flex-end" }}
                direction={{ base: "column", sm: "row" }}
                flexWrap="wrap"
            >
                {canViewMore && (
                    <Button
                        onClick={onViewMore}
                        borderRadius="5px"
                        color={theme.textOnPrimary}
                        bgImage={theme.gradients.primaryButton}
                        _hover={{
                            bgImage: theme.gradients.primaryButtonHover,
                        }}
                    >
                        View more
                    </Button>
                )}

                <ButtonGroup
                    isAttached
                    variant="outline"
                    display="flex"
                    flexWrap="wrap"
                    justifyContent={{ base: "center", sm: "flex-start" }}
                >
                    <IconButton
                        aria-label="Previous page"
                        icon={<Icon as={ChevronLeftIcon} />}
                        onClick={() => onPageChange(currentPage - 1)}
                        isDisabled={currentPage <= 1}
                        borderColor={theme.border}
                        color={theme.text}
                    />

                    {pageNumbers.map((page) => (
                        <Button
                            key={page}
                            onClick={() => onPageChange(page)}
                            bg={page === currentPage ? theme.primarySoft : theme.surface}
                            color={page === currentPage ? theme.primary : theme.text}
                            borderColor={
                                page === currentPage ? theme.primary : theme.border
                            }
                            fontWeight="900"
                            minW="42px"
                        >
                            {page}
                        </Button>
                    ))}

                    <IconButton
                        aria-label="Next page"
                        icon={<Icon as={ChevronRightIcon} />}
                        onClick={() => onPageChange(currentPage + 1)}
                        isDisabled={currentPage >= totalPages}
                        borderColor={theme.border}
                        color={theme.text}
                    />
                </ButtonGroup>
            </Flex>
        </Flex>
    );
};

const AllProducts: React.FC = () => {
    const { data: products = [], isLoading } = useGetProductsQuery();
    const combined = useCombinedStore() as any;
    const dispatch = useAppDispatch();
    const cart = useAppSelector((state) => state.cart);
    const {
        list: hikeTrips = [],
        loading: tripsLoading,
    } = useAppSelector((state) => state.hikeTrips);
    const navigate = useNavigate();
    const toast = useToast();
    const filterDrawer = useDisclosure();
    const { theme } = useCustomTheme();

    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [filtersByTab, setFiltersByTab] = useState<FiltersByTab>(
        createDefaultFiltersByTab
    );
    const [visibleLimit, setVisibleLimit] = useState(ITEMS_PER_PAGE);

    const accommodations = combined?.accommodations || [];
    const carHire = combined?.carHire || combined?.cars || [];

    useEffect(() => {
        dispatch(fetchAllTrips());
    }, [dispatch]);

    const futureTrips = useMemo(
        () => hikeTrips.filter(isFutureTrip),
        [hikeTrips]
    );

    const marketplaceTabs = useMemo<MarketplaceTab[]>(
        () => [
            {
                key: "products",
                label: `Products (${products.length})`,
                type: "products",
                items: products,
            },
            {
                key: "accommodations",
                label: `Accommodations (${accommodations.length})`,
                type: "accommodations",
                items: accommodations,
            },
            {
                key: "carHire",
                label: `Car Hire (${carHire.length})`,
                type: "carHire",
                items: carHire,
            },
            {
                key: "trips",
                label: `Trips (${futureTrips.length})`,
                type: "trips",
                items: futureTrips,
            },
        ],
        [products, accommodations, carHire, futureTrips]
    );

    const reusableMarketplaceTabs = useMemo<ReusableStyledTabItem[]>(
        () =>
            marketplaceTabs.map((tab) => ({
                key: tab.key,
                label: tab.label,
            })),
        [marketplaceTabs]
    );

    const activeTab = marketplaceTabs[activeTabIndex] || marketplaceTabs[0];
    const activeType = activeTab?.type || "products";
    const activeItems = activeTab?.items || [];
    const activeFilters = filtersByTab[activeType] || DEFAULT_FILTERS;

    const [minPrice, maxPrice] = useMemo(
        () => getPriceBounds(activeItems),
        [activeItems]
    );

    const categoryOptions = useMemo(
        () => getCategoryOptions(activeItems, activeType),
        [activeItems, activeType]
    );

    const filteredActiveItems = useMemo(
        () => applyListingFilters(activeItems, activeFilters, activeType),
        [activeItems, activeFilters, activeType]
    );

    const totalMarketplaceItems = useMemo(
        () =>
            products.length +
            accommodations.length +
            carHire.length +
            futureTrips.length,
        [products.length, accommodations.length, carHire.length, futureTrips.length]
    );

    const totalPages = Math.max(
        1,
        Math.ceil(filteredActiveItems.length / ITEMS_PER_PAGE)
    );

    const currentPage = Math.max(
        1,
        Math.min(Math.ceil(visibleLimit / ITEMS_PER_PAGE), totalPages)
    );

    const paginatedItems = useMemo(
        () => filteredActiveItems.slice(0, visibleLimit),
        [filteredActiveItems, visibleLimit]
    );

    const updateActiveFilters = (nextFilters: BusinessListingFilters) => {
        setFiltersByTab((current) => ({
            ...current,
            [activeType]: nextFilters,
        }));
    };

    useEffect(() => {
        const nextDefaultFilters = getDefaultFiltersForItems(activeItems);

        setFiltersByTab((current) => ({
            ...current,
            [activeType]: nextDefaultFilters,
        }));

        setVisibleLimit(ITEMS_PER_PAGE);
    }, [activeType, activeItems.length]);

    useEffect(() => {
        setVisibleLimit(ITEMS_PER_PAGE);
    }, [activeFilters]);

    const resetFilters = () => {
        updateActiveFilters(getDefaultFiltersForItems(activeItems));
        setVisibleLimit(ITEMS_PER_PAGE);
    };

    const handleTabChange = (index: number) => {
        setActiveTabIndex(index);
        setVisibleLimit(ITEMS_PER_PAGE);
    };

    const handlePageChange = (page: number) => {
        const safePage = Math.max(1, Math.min(page, totalPages));

        setVisibleLimit(safePage * ITEMS_PER_PAGE);
    };

    const handleViewMore = () => {
        setVisibleLimit((currentLimit) =>
            Math.min(currentLimit + ITEMS_PER_PAGE, filteredActiveItems.length)
        );
    };

    const handleAddProductToCart = (product: Product) => {
        const existing = cart.items.find((item) => item._id === product._id);

        if (!existing) {
            dispatch(addToCart(product));
        } else {
            dispatch(increaseQuantity(product._id!));
        }

        toast({
            title: existing ? "Cart updated" : "Added to cart",
            description: existing
                ? "Product quantity increased"
                : "Product added to cart",
            status: "success",
            duration: 2200,
            isClosable: true,
        });
    };

    if (isLoading) {
        return (
            <Box p={6} bg={theme.background} minH="100%">
                <Flex minH="260px" align="center" justify="center" direction="column" gap={3}>
                    <Spinner size="lg" color={theme.primary} />
                    <Text color={theme.textMuted} fontWeight="700">
                        Loading marketplace...
                    </Text>
                </Flex>
            </Box>
        );
    }

    return (
        <Box p={{ base: 4, md: 6 }} bg={theme.background} minH="100%">
            <AllProductsPageHeader
                totalItems={totalMarketplaceItems}
                filteredItems={filteredActiveItems.length}
            />

            {marketplaceTabs.length > 0 ? (
                <>
                    <Box mb={5}>
                        <Flex
                            align={{ base: "stretch", md: "center" }}
                            justify="space-between"
                            direction={{ base: "column", md: "row" }}
                            gap={3}
                        >
                            <Box flex="1" minW={0}>
                                <ReusableStyledTabs
                                    tabs={reusableMarketplaceTabs}
                                    index={activeTabIndex}
                                    onChange={handleTabChange}
                                    showPanels={false}
                                    width="full"
                                />
                            </Box>

                            <Button
                                display={{ base: "inline-flex", lg: "none" }}
                                leftIcon={<Icon as={FilterIcon} />}
                                onClick={filterDrawer.onOpen}
                                borderRadius="5px"
                                color={theme.textOnPrimary}
                                bgImage={theme.gradients.primaryButton}
                                _hover={{
                                    bgImage: theme.gradients.primaryButtonHover,
                                }}
                            >
                                Filters
                            </Button>
                        </Flex>
                    </Box>

                    <Flex align="flex-start" gap={5}>
                        <Box
                            display={{ base: "none", lg: "block" }}
                            w={{ lg: "300px", xl: "330px" }}
                            flexShrink={0}
                            position="sticky"
                            top="90px"
                        >
                            <ListingFilterSidebar
                                listingType={activeType}
                                filters={activeFilters}
                                categoryOptions={categoryOptions}
                                minPrice={minPrice}
                                maxPrice={maxPrice}
                                totalItems={activeItems.length}
                                filteredItems={filteredActiveItems.length}
                                onFiltersChange={updateActiveFilters}
                                onResetFilters={resetFilters}
                            />
                        </Box>

                        <Box flex="1" minW={0}>
                            <Flex
                                align={{ base: "flex-start", md: "center" }}
                                justify="space-between"
                                direction={{ base: "column", md: "row" }}
                                gap={3}
                                mb={4}
                                bg={theme.surface}
                                border={`1px solid ${theme.border}`}
                                borderRadius={cardsBorderRadii}
                                px={{ base: 4, md: 5 }}
                                py={3}
                            >
                                <Box>
                                    <Text
                                        color={theme.text}
                                        fontWeight="900"
                                        fontSize={{ base: "sm", md: "md" }}
                                    >
                                        {filteredActiveItems.length} result
                                        {filteredActiveItems.length === 1 ? "" : "s"}
                                    </Text>

                                    <Text
                                        color={theme.textMuted}
                                        fontSize="xs"
                                        fontWeight="700"
                                    >
                                        Showing {paginatedItems.length} of{" "}
                                        {filteredActiveItems.length}. Filtered from{" "}
                                        {activeItems.length} listing
                                        {activeItems.length === 1 ? "" : "s"}.
                                    </Text>
                                </Box>

                                <HStack>
                                    {activeType === "trips" && tripsLoading && (
                                        <Text
                                            color={theme.textMuted}
                                            fontSize="xs"
                                            fontWeight="800"
                                        >
                                            Refreshing trips...
                                        </Text>
                                    )}

                                    <Button
                                        display={{ base: "inline-flex", lg: "none" }}
                                        leftIcon={<Icon as={SlidersIcon} />}
                                        onClick={filterDrawer.onOpen}
                                        borderRadius="5px"
                                        variant="outline"
                                        borderColor={theme.border}
                                        color={theme.text}
                                    >
                                        Filter
                                    </Button>
                                </HStack>
                            </Flex>

                            {paginatedItems.length > 0 ? (
                                <>
                                    <ListingGrid
                                        items={paginatedItems}
                                        type={activeType}
                                        onAddProductToCart={handleAddProductToCart}
                                    />

                                    <ListingsPagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        visibleCount={paginatedItems.length}
                                        totalCount={filteredActiveItems.length}
                                        canViewMore={
                                            paginatedItems.length <
                                            filteredActiveItems.length
                                        }
                                        onPageChange={handlePageChange}
                                        onViewMore={handleViewMore}
                                    />
                                </>
                            ) : (
                                <EmptyFilteredState
                                    activeType={activeType}
                                    onResetFilters={resetFilters}
                                />
                            )}
                        </Box>
                    </Flex>

                    <Drawer
                        isOpen={filterDrawer.isOpen}
                        placement="bottom"
                        onClose={filterDrawer.onClose}
                        size="full"
                    >
                        <DrawerOverlay />
                        <DrawerContent
                            bg={theme.background}
                            borderTopRadius="28px"
                            maxH="92vh"
                            mt="auto"
                        >
                            <DrawerBody p={4} overflowY="auto">
                                <ListingFilterSidebar
                                    listingType={activeType}
                                    filters={activeFilters}
                                    categoryOptions={categoryOptions}
                                    minPrice={minPrice}
                                    maxPrice={maxPrice}
                                    totalItems={activeItems.length}
                                    filteredItems={filteredActiveItems.length}
                                    onFiltersChange={updateActiveFilters}
                                    onResetFilters={resetFilters}
                                    onClose={filterDrawer.onClose}
                                    showCloseButton
                                />
                            </DrawerBody>
                        </DrawerContent>
                    </Drawer>
                </>
            ) : (
                <Box
                    bg={theme.surface}
                    border={`1px solid ${theme.border}`}
                    borderRadius={cardsBorderRadii}
                    p={5}
                >
                    <Text color={theme.textMuted}>
                        No marketplace listings have been added yet.
                    </Text>

                    <Button
                        mt={4}
                        borderRadius="5px"
                        color={theme.textOnPrimary}
                        bgImage={theme.gradients.primaryButton}
                        _hover={{
                            bgImage: theme.gradients.primaryButtonHover,
                        }}
                        onClick={() => navigate("/manage/products")}
                    >
                        Manage products
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default AllProducts;